import React from 'react'
import { useTranslation } from 'react-i18next'
import Head, { PageHead } from '@fs/zion-head'
import { useLocation } from '@fs/zion-router'

const SeoPageHead = ({ seo }) => {
  const [t] = useTranslation()
  const { pathname } = useLocation()
  // NOTE: This does not handle all page titles. In some cases, the title needs specific data we don't have here.
  // SeriesPage, SessionPage, BoothPage, and SpeakerPage set their own title.
  const titleMap = {
    '/': t('navigation.home.label', 'Home'),
    '/search': t('navigation.on-demand.label', 'On Demand'),
    '/library': t('library.header', 'Video Library'),
    '/schedule/': t('navigation.schedule.label', 'Schedule'),
    '/playlist': t('navigation.playlists.label', 'My Playlists'),
    '/playlists/': t('navigation.playlists.label', 'My Playlists'),
    '/history': t('navigation.watch-history.label', 'Watch History'),
    '/dashboard/': t('navigation.dashboard.label', 'Dashboard'),
    '/dashboard/access': t('navigation.dashboard-access.label', 'Dashboard Access'),
    '/dashboard/authorization': t('navigation.dashboard-authorization.label', 'Dashboard Authorization'),
    '/user': t('navigation.user.label', 'User'),
    '/faq': t('navigation.faq.label', 'Frequently Asked Questions'),
    '/about': t('about.banner.heading', 'About Us'),

    '/subscription': t('conference-announce.subscribe.for.updates', 'Subscribe for Updates'),
    '/getting-started': t('search.getting-started.label', 'Getting Started'),

    '/registration/': t('event.reg.attend', 'Register for RootsTech'),
    '/registration/in-person/': t('event.reg.attend.in-person.register', 'In-Person Event Registration'),
    '/registration/online/': t('event.reg.attend.virtual.title', 'Online Event Registration'),
    '/registration/in-person/expohall/': t('event.reg.attend.expohall.register', 'Expo Hall Only Registration'),
    '/registration/in-person/discovery-day/': t(
      'event.reg.attend.discovery-day.register',
      'Discovery Day Registration'
    ),
    '/registration/online/arc/': t(
      'event.reg.attend.arc.register',
      'Archivist and Record Custodian Symposium Online Registration'
    ),
  }

  const clientTitle = titleMap[pathname] ?? t('site-title', 'RootsTech')

  if (!seo) {
    return null
  }
  const { twitter, canonicalLink, title, description } = seo || {}
  const seoTitle = title || clientTitle
  const seoDescription = description || ''
  const twitterUsername = twitter?.username !== '@FamilySearch' ? twitter?.username : '@RootsTechConf'
  const twitterImage = twitter?.image || {}
  const { openGraph } = seo
  const openGraphTitle = openGraph?.title || seoTitle
  const openGraphDescription = openGraph?.description || seoDescription
  const openGraphImage = openGraph?.image || {}
  const openGraphImageMetadata = openGraphImage?.metadata || {}

  return (
    <>
      <PageHead
        title={seoTitle}
        description={seoDescription}
        socialShareTitle={openGraphTitle}
        socialShareDescription={openGraphDescription}
        siteName={t('global.rootstech', 'RootsTech')}
        image={{
          alt: openGraphTitle,
          url: openGraphImage?.url,
          width: openGraphImageMetadata?.origWidthPixels || null, // 1200
          height: openGraphImageMetadata?.origHeightPixels || null, // 630
        }}
        twitterSite={twitterUsername}
        twitterCreator={twitterUsername}
        twitterCard={twitterImage?.url ? 'summary_large_image' : 'summary'}
        omitSiteName
      >
        {seo?.noIndexRobot && <meta name="robots" content="noindex" />}
        {seo?.noFollowRobot && <meta name="robots" content="nofollow" />}
        {seo?.noImageIndexRobot && <meta name="robots" content="noimageindex" />}
        {openGraphImage?.url && <meta property="og:image:type" content={getImageType(openGraphImage?.url)} />}
        <link rel="canonical" href={canonicalLink || window?.location?.href} />
      </PageHead>
      <Head>
        {seo?.noIndexRobot && <meta name="robots" content="noindex" />}
        {seo?.noFollowRobot && <meta name="robots" content="nofollow" />}
        {seo?.noImageIndexRobot && <meta name="robots" content="noimageindex" />}
        {openGraphImage?.url && <meta property="og:image:type" content={getImageType(openGraphImage?.url)} />}
        <link rel="canonical" href={canonicalLink || window?.location?.href} />
      </Head>
    </>
  )
}

function getImageType(imageUrl = '') {
  let imageType = 'image/jpeg'
  if (imageUrl.match(/\.png/gi)) imageType = 'image/png'
  if (imageUrl.match(/\.gif/gi)) imageType = 'image/gif'
  if (imageUrl.match(/\.svg/gi)) imageType = 'image/svg+xml'
  return imageType
}

export default SeoPageHead
